<script lang="ts">
    import Fa from 'svelte-fa'
    import { faAngleLeft, faBalanceScale } from '@fortawesome/free-solid-svg-icons'
    import Content from '../_common/content.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Popover from '../_common/popover.svelte'
    import StockAdjustForm from './stock-adjust-form.svelte'
    import { onMount } from 'svelte'
    import type { Person } from '../../modules/eventsApi/persons/person'
    import { PersonsRepository } from '../../modules/eventsApi/persons/persons.repository'
    import { PersonsService } from '../../modules/eventsApi/persons/persons.service'
    import dayjs from 'dayjs'
    import DateSelect from '../_common/date-select.svelte'
    import { ProductMovementsRepository } from '../../modules/posApi/stock/product-movements/product-movements.repository'
    import { ProductMovementsService } from '../../modules/posApi/stock/product-movements/product-movements.service'
    import type { ProductMovement } from '../../modules/posApi/stock/product-movements/product-movement'
    import { productMovementReasonOptions } from '../../modules/posApi/stock/product-movements/product-movement-reason.enum'
    import type { Service } from '../../modules/posApi/services/service'
    import { ServicesRepository } from '../../modules/posApi/services/services.repository'
    import { ServicesService } from '../../modules/posApi/services/services.service'
    import { GridBadge, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../_common/z/zgrid'
    import Zgrid from '../_common/z/zgrid.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'

    const repProd = new ServicesRepository()
    const servProd = new ServicesService(repProd)
    let entities: Service[] = []

    const rep = new ProductMovementsRepository()
    const service = new ProductMovementsService(rep)

    let personsR = new PersonsRepository()
    let personsS = new PersonsService(personsR)

    let show = false
    let movements: ProductMovement[] = []
    let dates: { from: Date; to: Date } = window.api.utils.dates.today()
    resetDate()
    let dateSelect
    let step = 1

    onMount(async () => {
        await loadEntities()
    })

    async function loadEntities() {
        entities = []
        entities = await servProd.getAll({ type: ['product', 'consumable'] })
        entities = entities.filter((el) => el.balance)
        loadGrid()
    }

    async function loadDetails() {
        try {
            show = true
            movements = []
            const filter = {
                from: dates?.from || undefined,
                to: dates?.to || undefined,
                serviceId: serviceId,
            }

            // limita a navegação por mais de 7 dias
            if (!window.store.token.permissions.pos.sales.navigate) {
                const diff = dayjs(new Date()).diff(filter.from, 'days')
                if (diff > 7) {
                    dates.from = dayjs(new Date()).add(-7, 'day').toDate()
                    filter.from = dates.from
                    dateSelect.setDate(dates.from, dates.to)
                }
            }

            movements = await service.getAll(filter)
            let persons: Person[] = []
            let personsId = []
            for (const invoice of movements) {
                personsId.push(invoice.personId)
            }

            let promises = []
            for (let i = 0; i < personsId.length; i += 100) {
                const chunk = personsId.slice(i, i + 100)
                promises.push(personsS.getAll({ persons: chunk.join(',') }))
            }
            const results = await Promise.all(promises)
            for (const result of results) {
                persons = persons.concat(result)
            }
            for (let invoice of movements) {
                invoice.person = persons.find((el) => el.personId === invoice.personId)
            }
            loadMovementGrids()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        grid = new GridClass()
        grid.title = 'Estoque'
        grid.subtitle =
            'O cálculo do próximo vencimento assume que os itens mais próximos de vencer são consumidos/vendidos primeiro. "Valor em estoque" usa o custo médio ponderado das entradas com nota de compra.'
        grid.cols.push(new GridCol({ content: 'Item' }))
        grid.cols.push(new GridCol({ content: 'Tipo', type: GridColTypeEnum.badge, size: 1 }))
        //grid.cols.push(new GridCol({ content: 'Próximo vencimento', type: GridColTypeEnum.datetime_friendly, size: 1 }))
        grid.cols.push(new GridCol({ content: 'Estoque atual', type: GridColTypeEnum.float_2, size: 1 }))
        //grid.cols.push(new GridCol({ content: 'Estoque mínimo', type: GridColTypeEnum.float_2, size: 1 }))
        grid.cols.push(new GridCol({ content: 'Valor em estoque', type: GridColTypeEnum.float_2, size: 2, showTotal: true }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button, size: 2 }))
        for (const item of entities) {
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.name })
            line.slots.push({ content: typeBadge(item) })
            //line.slots.push({ content: item.nextExpirationDate })
            line.slots.push({ content: item.stock })
            //line.slots.push({ content: item.minStock })
            line.slots.push({ content: item.stock * (item.avgCost || 0) })
            const buttons = [
                new GridButton({
                    label: 'Movimentações',
                    callback: () => {
                        showMovements(item.serviceId)
                    },
                }),
            ]
            if (window.store.token.permissions.stock.inputs.write && window.store.token.permissions.stock.outputs.write) {
                buttons.push(
                    new GridButton({
                        label: 'Ajuste',
                        icon: faBalanceScale,
                        callback: () => {
                            openAdjust(item)
                        },
                    }),
                )
            }
            line.slots.push({ content: buttons })
            grid.lines.push(line)
        }
        grid = grid
    }

    let adjustEntity: Service = null
    function openAdjust(item: Service) {
        adjustEntity = item
    }
    function closeAdjust() {
        adjustEntity = null
    }
    async function adjustSaved() {
        adjustEntity = null
        await loadEntities()
    }

    function typeBadge(item: Service): GridBadge {
        if (item.type === 'consumable') return new GridBadge({ label: 'Insumo', color: '#00897b' })
        return new GridBadge({ label: 'Produto', color: '#1e88e5' })
    }

    function originBadge(item: ProductMovement): GridBadge {
        if (item.invoiceId) return new GridBadge({ label: 'PDV', color: '#645ec9' })
        if (item.type === 'out_manual') return new GridBadge({ label: 'Saída manual', color: '#b67503' })
        if (item.type === 'in') return new GridBadge({ label: 'Compra', color: '#0caa50' })
        if (item.type === 'out_consumption') return new GridBadge({ label: 'Consumo', color: '#00897b' })
        if (item.type === 'out_open') return new GridBadge({ label: 'Abertura de frasco', color: '#00acc1' })
        return new GridBadge({ label: 'Desconhecido', color: '#666' })
    }

    function reasonLabel(reason: string) {
        if (!reason) return '-'
        return productMovementReasonOptions.find((el) => el.value === reason)?.label || '-'
    }

    let inGrid: GridClass = new GridClass()
    let outGrid: GridClass = new GridClass()
    function loadMovementGrids() {
        const inItems = movements.filter((el) => el.type === 'in')
        const outItems = movements.filter((el) => el.type !== 'in')

        const _inGrid = new GridClass()
        _inGrid.title = 'Entradas'
        _inGrid.cols.push(new GridCol({ content: 'Origem', type: GridColTypeEnum.badge, size: 1 }))
        _inGrid.cols.push(new GridCol({ content: 'Lançamento', type: GridColTypeEnum.datetime, size: 2 }))
        _inGrid.cols.push(new GridCol({ content: 'Entrada', type: GridColTypeEnum.datetime, size: 2 }))
        _inGrid.cols.push(new GridCol({ content: 'Vencimento', type: GridColTypeEnum.date, size: 2 }))
        _inGrid.cols.push(new GridCol({ content: 'Fornecedor' }))
        _inGrid.cols.push(new GridCol({ content: 'Motivo' }))
        _inGrid.cols.push(new GridCol({ content: 'Vlr unit', type: GridColTypeEnum.float_2, size: 1 }))
        _inGrid.cols.push(new GridCol({ content: 'Qtde', type: GridColTypeEnum.float_2, size: 1 }))
        _inGrid.cols.push(new GridCol({ content: 'Total', type: GridColTypeEnum.float_2, size: 1 }))
        for (const item of inItems) {
            const line = new GridLine()
            line.slots.push({ content: originBadge(item) })
            line.slots.push({ content: item.createdAt })
            line.slots.push({ content: item.movedAt })
            line.slots.push({ content: item.expiresAt })
            line.slots.push({ content: item.person?.name || '-' })
            line.slots.push({ content: reasonLabel(item.reason) })
            line.slots.push({ content: item.value })
            line.slots.push({ content: item.quantity })
            line.slots.push({ content: item.value * item.quantity })
            _inGrid.lines.push(line)
        }
        inGrid = _inGrid

        const _outGrid = new GridClass()
        _outGrid.title = 'Saídas'
        _outGrid.cols.push(new GridCol({ content: 'Origem', type: GridColTypeEnum.badge, size: 1 }))
        _outGrid.cols.push(new GridCol({ content: 'Lançamento', type: GridColTypeEnum.datetime, size: 2 }))
        _outGrid.cols.push(new GridCol({ content: 'Saída', type: GridColTypeEnum.datetime, size: 2 }))
        _outGrid.cols.push(new GridCol({ content: 'Cliente' }))
        _outGrid.cols.push(new GridCol({ content: 'Motivo' }))
        _outGrid.cols.push(new GridCol({ content: 'Vlr unit', type: GridColTypeEnum.float_2, size: 1 }))
        _outGrid.cols.push(new GridCol({ content: 'Qtde', type: GridColTypeEnum.float_2, size: 1 }))
        _outGrid.cols.push(new GridCol({ content: 'Total', type: GridColTypeEnum.float_2, size: 1 }))
        for (const item of outItems) {
            const line = new GridLine()
            line.slots.push({ content: originBadge(item) })
            line.slots.push({ content: item.createdAt })
            line.slots.push({ content: item.movedAt })
            line.slots.push({ content: item.person?.name || '-' })
            line.slots.push({ content: reasonLabel(item.reason) })
            line.slots.push({ content: item.value })
            line.slots.push({ content: item.quantity })
            line.slots.push({ content: item.value * item.quantity })
            _outGrid.lines.push(line)
        }
        outGrid = _outGrid
    }

    function evaluateError(error) {
        window.error(error)
    }

    async function dateChanged(event) {
        dates = event.detail
        await loadDetails()
    }

    let serviceId: string = null
    let selectedName: string = ''
    function showMovements(id) {
        resetDate()
        serviceId = id
        selectedName = entities.find((el) => el.serviceId === id)?.name || ''
        step = 2
        loadDetails()
    }

    function showProducts() {
        step = 1
        serviceId = null
    }
    function resetDate() {
        dates = window.api.utils.dates.currentMonth()
    }
</script>

{#if adjustEntity}
    <Popover title="Ajuste de estoque" width="600px" height="auto" on:close={closeAdjust}>
        <StockAdjustForm entity={adjustEntity} on:change={adjustSaved} on:cancel={closeAdjust} />
    </Popover>
{/if}

<Content footer={true}>
    {#if step === 1}
        <Zgrid class="h-space" {grid} />
    {/if}
    {#if step === 2}
        <Zact>
            <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={showProducts} />
            <div class="stock_current">{selectedName}</div>
            <div class="spacer" />
            <DateSelect bind:this={dateSelect} startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        </Zact>
        <Zgrid class="h-space mb-4" grid={inGrid} />
        <Zgrid class="h-space mb-4" grid={outGrid} />
    {/if}
    <LoadSpinner {show} />
</Content>

<style>
    .stock_current {
        font-size: 18px;
        font-weight: 300;
    }
</style>
